import React, { memo } from 'react';
import { TrendingUp, TrendingDown, Layers, Sparkles } from 'lucide-react';
import { IndexSymbol, type IndexConstituentsSummary } from '../../config/constants';
import { NumberTicker } from '../ui/NumberTicker';
import { useMarketStore } from '../../services/store';

interface IndexPlCardProps {
    summary: IndexConstituentsSummary;
    isActive?: boolean;
    onSelect?: () => void;
}

const INDEX_ACCENT_COLORS: Record<IndexSymbol, {
    border: string;
    activeBorder: string;
    badge: string;
    iconBg: string;
    iconColor: string;
    glow: string;
}> = {
    [IndexSymbol.NIFTY]: {
        border: 'border-blue-200/60 dark:border-blue-900/40',
        activeBorder: 'border-blue-500 dark:border-blue-400 ring-2 ring-blue-500/40 dark:ring-blue-400/40',
        badge: 'bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border-blue-200/80 dark:border-blue-800/60',
        iconBg: 'bg-blue-500/10 dark:bg-blue-400/10',
        iconColor: 'text-blue-600 dark:text-blue-400',
        glow: 'hover:shadow-[0_8px_25px_-6px_rgba(59,130,246,0.2)]',
    },
    [IndexSymbol.BANKNIFTY]: {
        border: 'border-purple-200/60 dark:border-purple-900/40',
        activeBorder: 'border-purple-500 dark:border-purple-400 ring-2 ring-purple-500/40 dark:ring-purple-400/40',
        badge: 'bg-purple-50 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300 border-purple-200/80 dark:border-purple-800/60',
        iconBg: 'bg-purple-500/10 dark:bg-purple-400/10',
        iconColor: 'text-purple-600 dark:text-purple-400',
        glow: 'hover:shadow-[0_8px_25px_-6px_rgba(168,85,247,0.2)]',
    },
    [IndexSymbol.SENSEX]: {
        border: 'border-amber-200/60 dark:border-amber-900/40',
        activeBorder: 'border-amber-500 dark:border-amber-400 ring-2 ring-amber-500/40 dark:ring-amber-400/40',
        badge: 'bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border-amber-200/80 dark:border-amber-800/60',
        iconBg: 'bg-amber-500/10 dark:bg-amber-400/10',
        iconColor: 'text-amber-600 dark:text-amber-400',
        glow: 'hover:shadow-[0_8px_25px_-6px_rgba(245,158,11,0.2)]',
    },
};

const getIndexHeading = (index: IndexSymbol): string => {
    switch (index) {
        case IndexSymbol.NIFTY:
            return 'NIFTY 50';
        case IndexSymbol.BANKNIFTY:
            return 'BANK NIFTY';
        case IndexSymbol.SENSEX:
            return 'SENSEX';
        default:
            return String(index);
    }
};

export const IndexPlCard: React.FC<IndexPlCardProps> = memo(({
    summary,
    isActive = false,
    onSelect,
}) => {
    const isGain = summary.totalDayChange >= 0;
    const colors = INDEX_ACCENT_COLORS[summary.index] || INDEX_ACCENT_COLORS[IndexSymbol.NIFTY];

    // Live Market Store Sync for LTP and Day Change
    const storeIndexLtp = useMarketStore((s) => s.indexLtp?.[summary.index]);
    const storeMarketMeta = useMarketStore((s) => s.marketMeta?.[summary.index]);

    const effectiveLtp = (storeIndexLtp && storeIndexLtp > 0) ? storeIndexLtp : (summary.indexLtp || 0);
    const effectiveDayChange = (storeMarketMeta?.dayChange !== undefined && storeMarketMeta.dayChange !== 0)
        ? storeMarketMeta.dayChange
        : (summary.indexDayChange || 0);
    const effectiveDayChangePercent = (storeMarketMeta?.dayChangePercent !== undefined && storeMarketMeta.dayChangePercent !== 0)
        ? storeMarketMeta.dayChangePercent
        : (storeMarketMeta?.dayChangeP !== undefined && storeMarketMeta.dayChangeP !== 0)
        ? storeMarketMeta.dayChangeP
        : (summary.indexDayChangePercent || 0);

    const isIndexGain = effectiveDayChange >= 0;

    // Compute breadth percentage with single decimal precision
    const totalValid = (summary.advances + summary.declines + summary.unchanged) || summary.totalStocks || 1;
    const advPercent = ((summary.advances / totalValid) * 100).toFixed(1);
    const decPercent = ((summary.declines / totalValid) * 100).toFixed(1);
    const advWidth = Math.max(0, (summary.advances / totalValid) * 100);
    const decWidth = Math.max(0, (summary.declines / totalValid) * 100);

    // Previous Day Index Close for Weighted Projection Tooltip
    const prevClose = summary.indexPrevClose || 0;

    return (
        <div
            onClick={onSelect}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect?.();
                }
            }}
            className={`relative flex flex-col h-full p-2.5 sm:p-3 rounded-2xl bg-white/95 dark:bg-slate-900/90 backdrop-blur-md border transition-all duration-250 cursor-pointer group select-none overflow-hidden ${
                isActive
                    ? `${colors.activeBorder} shadow-[0_10px_28px_-6px_rgba(99,102,241,0.25)] bg-gradient-to-br from-white via-slate-50 to-indigo-50/20 dark:from-slate-900 dark:via-slate-900 dark:to-indigo-950/20`
                    : `border-slate-200/80 dark:border-slate-800/80 shadow-xs hover:border-slate-300 dark:hover:border-slate-700 ${colors.glow}`
            }`}
        >
            {/* Row 1: Index Identity & Active Badge on Left + Spot LTP on Right */}
            <div className="flex items-center justify-between w-full min-w-0">
                <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                    <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${colors.iconBg} ${colors.iconColor} shrink-0 shadow-2xs`}>
                        <Layers size={14} strokeWidth={2.5} />
                    </div>
                    <div className="flex items-center gap-1 min-w-0">
                        <span className="text-[12.5px] sm:text-[13.5px] font-black text-slate-900 dark:text-slate-50 tracking-tight leading-none whitespace-nowrap">
                            {getIndexHeading(summary.index)}
                        </span>
                        {isActive && (
                            <span className="inline-flex items-center gap-0.5 text-[7.5px] sm:text-[8px] font-black bg-indigo-600 text-white px-1.5 py-0.2 rounded-full uppercase tracking-wider shadow-2xs shrink-0">
                                <Sparkles size={7} /> Active
                            </span>
                        )}
                    </div>
                </div>

                {effectiveLtp > 0 && (
                    <div className="flex items-baseline gap-0.5 shrink-0 font-mono text-right">
                        <span className="text-[8px] font-bold text-slate-400 dark:text-slate-500 font-sans uppercase">LTP</span>
                        <span className="text-[12px] sm:text-[13px] font-black text-slate-900 dark:text-slate-100 tracking-tight">
                            ₹<NumberTicker value={effectiveLtp} decimals={2} />
                        </span>
                    </div>
                )}
            </div>

            {/* Row 2: Exchange & Stocks Badge on Left + Day Change % on Right */}
            <div className="flex items-center justify-between w-full mt-1 mb-2 pt-1 border-t border-slate-100 dark:border-slate-800/60 min-w-0">
                <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                        {summary.exchange}
                    </span>
                    <span className="text-slate-300 dark:text-slate-700 text-[8px]">•</span>
                    <span className={`text-[8.5px] font-extrabold px-1.5 py-0.2 rounded-md border shrink-0 whitespace-nowrap ${colors.badge}`}>
                        {summary.totalStocks} Stocks
                    </span>
                </div>

                {effectiveLtp > 0 && (
                    <div className={`flex items-center gap-0.5 text-[9px] sm:text-[9.5px] font-black font-mono shrink-0 px-1 py-0.2 rounded ${
                        isIndexGain 
                            ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200/80 dark:border-emerald-800/60' 
                            : 'text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/60 border border-rose-200/80 dark:border-rose-800/60'
                    }`}>
                        {isIndexGain ? (
                            <TrendingUp size={9} strokeWidth={3} className="shrink-0" />
                        ) : (
                            <TrendingDown size={9} strokeWidth={3} className="shrink-0" />
                        )}
                        <span>{isIndexGain ? '+' : ''}{effectiveDayChange.toFixed(2)}</span>
                        <span className="opacity-75">({isIndexGain ? '+' : ''}{effectiveDayChangePercent.toFixed(2)}%)</span>
                    </div>
                )}
            </div>

            {/* Row 3: Day P&L Sum, Projected2 Chip & Avg % */}
            <div className="flex items-end justify-between w-full mb-2 min-w-0">
                <div className="flex flex-col min-w-0">
                    <span className="text-[8.5px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider leading-none">
                        Day P&L Sum
                    </span>
                    <div className="flex flex-wrap items-center gap-1.5 mt-0.5 min-w-0">
                        <div className={`flex items-baseline text-[16px] sm:text-[18px] font-black font-mono tracking-tight leading-tight shrink-0 ${
                            isGain ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
                        }`}>
                            <span className="text-[13px] mr-0.5">{isGain ? '+₹' : '-₹'}</span>
                            <NumberTicker
                                value={Math.abs(summary.totalDayChange)}
                                decimals={2}
                            />
                        </div>

                        {/* Projected2 Chip */}
                        {summary.projectedIndex2 && summary.projectedIndex2 > 0 && (
                            <div
                                className="inline-flex items-center gap-0.5 text-[9.5px] sm:text-[10px] font-black font-mono px-1.5 py-0.2 rounded-md bg-purple-50/90 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300 border border-purple-200/90 dark:border-purple-800/60 shadow-2xs shrink-0"
                                title={`Method 2 (Weighted): Prev Close (₹${prevClose.toFixed(2)}) + Weighted Move (${summary.projectedWeightedPts && summary.projectedWeightedPts >= 0 ? '+' : ''}${summary.projectedWeightedPts?.toFixed(2)} pts)`}
                            >
                                <span className="text-[7.5px] font-sans opacity-70 uppercase tracking-tighter">Proj2:</span>
                                <span>₹</span>
                                <NumberTicker value={summary.projectedIndex2} decimals={2} />
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex flex-col items-end shrink-0 ml-1.5">
                    <span className="text-[8.5px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider leading-none">
                        Avg %
                    </span>
                    <div className={`flex items-center gap-0.5 text-[10px] sm:text-[10.5px] font-black font-mono px-1.5 py-0.5 rounded-md border shrink-0 mt-0.5 ${
                        summary.avgDayChangePercent >= 0
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300 border-emerald-200/80 dark:border-emerald-800/50'
                            : 'bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300 border-rose-200/80 dark:border-rose-800/50'
                    }`}>
                        {summary.avgDayChangePercent >= 0 ? (
                            <TrendingUp size={10} strokeWidth={3} className="shrink-0" />
                        ) : (
                            <TrendingDown size={10} strokeWidth={3} className="shrink-0" />
                        )}
                        <span>
                            {summary.avgDayChangePercent >= 0 ? '+' : ''}
                            <NumberTicker value={summary.avgDayChangePercent} decimals={2} />%
                        </span>
                    </div>
                </div>
            </div>

            {/* Row 4: Breadth Bar with Advance/Decline Percentages */}
            <div className="w-full mt-auto pt-1.5 border-t border-slate-100 dark:border-slate-800/70">
                <div className="flex items-center justify-between text-[9px] sm:text-[9.5px] font-bold mb-1">
                    {/* Advance Count + Percentage */}
                    <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1 shrink-0 whitespace-nowrap">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0 shadow-[0_0_6px_rgba(16,185,129,0.6)]" />
                        <span>▲ {summary.advances} Adv <span className="font-mono text-[8.5px] opacity-90">({advPercent}%)</span></span>
                    </span>

                    {/* Decline Count + Percentage */}
                    <span className="text-rose-600 dark:text-rose-400 flex items-center gap-1 shrink-0 whitespace-nowrap">
                        <span>▼ {summary.declines} Dec <span className="font-mono text-[8.5px] opacity-90">({decPercent}%)</span></span>
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0 shadow-[0_0_6px_rgba(244,63,94,0.6)]" />
                    </span>
                </div>

                {/* Ultra-Slim Gradient Progress Track */}
                <div className="w-full h-1 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden flex shadow-inner">
                    <div
                        className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-500 ease-out rounded-l-full"
                        style={{ width: `${advWidth}%` }}
                        title={`${summary.advances} Advances (${advPercent}%)`}
                    />
                    <div
                        className="h-full bg-gradient-to-r from-rose-400 to-rose-500 transition-all duration-500 ease-out rounded-r-full ml-auto"
                        style={{ width: `${decWidth}%` }}
                        title={`${summary.declines} Declines (${decPercent}%)`}
                    />
                </div>
            </div>
        </div>
    );
});

IndexPlCard.displayName = 'IndexPlCard';
